import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, getFetchConfig } from '../config/api';
import AddToInterestButton from '../components/AddToInterestButton';
import ProductImageCarousel from '../components/ProductImageCarousel';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';
import { extractProductIdFromUrl, generateProductBreadcrumbs } from '../utils/slugify';
import { trackProductView } from '../utils/analytics';
import './ProductDetail.css';

const ProductDetail = () => {
  const { id, slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Determinar o ID do produto com base na URL
  const getProductId = React.useCallback(() => {
    if (id) {
      // URL antiga: /product/:id
      return id;
    } else if (slug) {
      // URL nova: /produtos/:category/:slug
      const extractedId = extractProductIdFromUrl(window.location.pathname);
      return extractedId;
    }
    return null;
  }, [id, slug]);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        setLoading(true);
        const productId = getProductId();
        
        if (!productId) {
          setError('ID do produto não encontrado');
          return;
        }

        const response = await fetch(`${API_ENDPOINTS.PRODUCTS}/${productId}`, getFetchConfig('GET'));
        const data = await response.json();
        
        if (data.status === 'ok') {
          setProduct(data.product);
          // Track visualização do produto
          trackProductView(data.product.id, data.product.name, data.product.category);
        } else {
          setError(data.error || 'Produto não encontrado');
        }
      } catch (error) {
        setError('Erro ao carregar produto: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id, slug, getProductId]);

  const handleRequestQuote = async () => {
    // Função removida - agora usamos apenas o botão de adicionar à lista de interesses
  };

  if (loading) {
    return (
      <div className="product-detail-container">
        <div className="loading">Carregando produto...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-detail-container">
        <div className="error-message">{error}</div>
        <button onClick={() => navigate('/shop')} className="btn-back">
          Voltar à Loja
        </button>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-container">
        <div className="error-message">Produto não encontrado</div>
        <button onClick={() => navigate('/shop')} className="btn-back">
          Voltar à Loja
        </button>
      </div>
    );
  }

  return (
    <div className="product-detail-container">
      <SEOHead
        title={`${product.name} - MercadoTV | Sistemas de Segurança`}
        description={`${product.description} | Instalação profissional incluída. Orçamentos gratuitos na MercadoTV.`}
        keywords={`${product.name}, ${product.category}, videovigilância, segurança, ${product.name.toLowerCase()}, sistemas segurança portugal`}
        canonicalUrl={`https://mercadotv.pt/product/${product.id}`}
        ogImage={product.image || '/logo.png'}
        ogType="product"
        isProduct={true}
        productData={{
          name: product.name,
          description: product.description,
          image: product.image,
          category: product.category
        }}
      />
      
      <Breadcrumbs 
        customItems={generateProductBreadcrumbs(window.location.pathname, product)}
      />
      
      <button onClick={() => navigate('/shop')} className="btn-back">
        ← Voltar à Loja
      </button>
      
      <div className="product-detail-content">
        <div className="product-image-section">
          <ProductImageCarousel 
            images={product.images} 
            productName={product.name}
          />
        </div>
        
        <div className="product-info-section">
          <div className="product-header">
            <div className="product-title-section">
              <h1 className="product-title">{product.name}</h1>
              <div className="product-category">
                <span className="category-badge">{product.category}</span>
              </div>
            </div>
            
            <div className="product-cta-section">
              <AddToInterestButton 
                productId={product.id}
                className="detail-page-primary"
                size="large"
                showText={true}
              />
              <p className="cta-description">
                Adicione à sua lista de interesses para solicitar orçamento personalizado
              </p>
            </div>
          </div>
          
          <div className="product-description">
            <h3>Descrição</h3>
            <p>{product.description}</p>
          </div>
          
          <div className="product-features">
            <h3>Características</h3>
            <ul>
              <li>Categoria: {product.category}</li>
              <li>Disponibilidade: Disponível</li>
              <li>Instalação profissional incluída</li>
              <li>Garantia do fabricante</li>
              <li>Suporte técnico especializado</li>
            </ul>
          </div>
          
          <div className="product-benefits">
            <h3>Vantagens MercadoTV</h3>
            <ul>
              <li>✓ Sem mensalidades</li>
              <li>✓ Custo único de compra/instalação</li>
              <li>✓ Atendimento personalizado</li>
              <li>✓ Orçamento sem compromisso</li>
              <li>✓ 15+ anos de experiência</li>
            </ul>
          </div>
          
          <div className="contact-info-card">
            <h4>Precisa de Ajuda?</h4>
            <p>📞 <strong>Tel:</strong> +351 256 418 202</p>
            <p>📧 <strong>Email:</strong> mlptelecomunicacoes@gmail.com</p>
            <p>📍 <strong>Localização:</strong> Vale de Cambra, Aveiro</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
